import { Injectable } from '@angular/core';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import {
  FormSizeType,
  FormState,
  FormType,
  FormZIndexType
} from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import {
  formInitialState,
  formsInitialState
} from '@zaoblako/web/shared/components/form-sidebar/state/form-initial-state';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FormStoreService extends StoreService<FormState> {
  protected state = new BehaviorSubject(formsInitialState);

  public get getState() {
    return this.state.value;
  }

  public constructor(private readonly accessService: AccessService) {
    super();
  }

  public saveNext<T = any>(entity: T, index: number) {
    this.getState.forms[index]?.onSave$.next(entity);
    this.closeSidebar(index);
  }

  public deleteNext(index: number) {
    this.getState.forms[index]?.onDelete$.next();
    this.closeSidebar(index);
  }

  public addForm<T = any>(
    type: TypeFormEnum,
    options: {
      headerText?: string;
      widthSize?: FormSizeType;
      isNotShowRedirectInCreate?: boolean;
      typeZIndex?: FormZIndexType;
      data?: any;
    }
  ): { onSave: Observable<T>; onDelete: Observable<void> } {
    const updatedState: FormType = {
      ...formInitialState,
      isShowSidebar: true,
      onSave$: new Subject(),
      onDelete$: new Subject(),
      type,
      canWrite: this.checkWriteAccessForFormType(type)
    };
    if (options?.headerText) {
      updatedState.headerText = options.headerText;
    }
    if (options?.widthSize) {
      updatedState.widthSize = options.widthSize;
    }
    if (options?.typeZIndex) {
      updatedState.typeZIndex = options.typeZIndex;
    }
    if (options?.isNotShowRedirectInCreate) {
      updatedState.isShowRedirectInCreate = false;
      updatedState.isRedirectInCreate = false;
    }
    if (options?.data) {
      updatedState.data = options.data;
    }

    this.updateState((state) => ({
      forms: [...state.forms, updatedState]
    }));
    return { onSave: updatedState.onSave$.asObservable(), onDelete: updatedState.onDelete$.asObservable() };
  }

  public editForm<T = any>(
    type: TypeFormEnum,
    entityId: number,
    options: {
      headerText?: string;
      widthSize?: FormSizeType;
      isNotShowDelete?: boolean;
      isShowShare?: boolean;
      typeZIndex?: FormZIndexType;
      data?: any;
    }
  ): { onSave: Observable<T>; onDelete: Observable<void> } {
    const updatedState: FormType = {
      ...formInitialState,
      isShowSidebar: true,
      loading: true,
      isEdit: true,
      isShowDelete: true,
      onSave$: new Subject(),
      onDelete$: new Subject(),
      entityId,
      type,
      canWrite: this.checkWriteAccessForFormType(type)
    };
    if (options?.headerText) {
      updatedState.headerText = options.headerText;
    }
    if (options?.widthSize) {
      updatedState.widthSize = options.widthSize;
    }
    if (options?.typeZIndex) {
      updatedState.typeZIndex = options.typeZIndex;
    }
    if (options?.isNotShowDelete) {
      updatedState.isShowDelete = false;
    }
    if (options?.isShowShare) {
      updatedState.isShowShare = true;
    }
    if (options?.data) {
      updatedState.data = options.data;
    }

    this.updateState((state) => ({
      forms: [...state.forms, updatedState]
    }));
    return { onSave: updatedState.onSave$.asObservable(), onDelete: updatedState.onDelete$.asObservable() };
  }

  public setLoading(loading: boolean, index: number) {
    const forms = [...this.getState.forms];
    if (forms[index]) {
      forms[index].loading = loading;
    }
    this.updateState({
      forms
    });
  }

  public closeSidebar(index: number) {
    const forms = [...this.getState.forms];
    forms[index].onSave$.unsubscribe();
    forms[index].onDelete$.unsubscribe();
    forms.splice(index, 1);
    this.updateState({
      forms
    });
  }

  public toggleIsRedirectInCreate(value: boolean, index: number) {
    const forms = [...this.getState.forms];
    forms[index].isRedirectInCreate = value;
    this.updateState({
      forms
    });
  }

  public extractFormParams(url: string) {
    const urlObj = new URL(url);
    const searchParams = urlObj.searchParams;

    return {
      formType: searchParams.get('formType') as TypeFormEnum,
      formId: +searchParams.get('formId'),
      formHeaderText: decodeURIComponent(searchParams.get('formHeaderText') || ''),
      formWidthSize: searchParams.get('formWidthSize') as FormSizeType,
      formIsShowDelete: searchParams.get('formIsShowDelete')?.toLowerCase() === 'true',
      formIsShowShare: searchParams.get('formIsShowShare')?.toLowerCase() === 'true',
      formTypeZIndex: searchParams.get('formTypeZIndex') as FormZIndexType
    };
  }

  private checkWriteAccessForFormType(formType: TypeFormEnum): boolean {
    switch (formType) {
      case TypeFormEnum.BANNER:
        return this.accessService.basis.clientRights.canWriteBanner;
      case TypeFormEnum.USER_GROUP:
        return this.accessService.basis.securityRights.canWriteGroup;
      case TypeFormEnum.DIRECTION:
      case TypeFormEnum.DIRECTION_GROUP:
        return this.accessService.basis.catalogRights.canWriteDirection;
      case TypeFormEnum.UNIT:
        return this.accessService.basis.catalogRights.canWriteUnit;
      case TypeFormEnum.FILIAL:
        return this.accessService.basis.catalogRights.canWriteFilial;
      case TypeFormEnum.ROOM:
        return this.accessService.basis.catalogRights.canWriteRoom;
      case TypeFormEnum.DOCUMENT_TYPE:
        return this.accessService.basis.catalogRights.canWriteDocumentType;
      case TypeFormEnum.OPERATION_TYPE:
      case TypeFormEnum.OPERATION_TYPE_ERP_1C:
        return this.accessService.basis.catalogRights.canWriteOperationType;
      case TypeFormEnum.MASTER_MILESTONE:
        return this.accessService.basis.catalogRights.canWriteMasterMilestone;
      case TypeFormEnum.APPROVAL_SCHEME:
        return this.accessService.basis.catalogRights.canWriteApprovalScheme;
      case TypeFormEnum.NOMENCLATURE:
      case TypeFormEnum.NOMENCLATURE_GROUP:
        return this.accessService.basis.catalogRights.canWriteNomenclature;
      case TypeFormEnum.COST_CENTER:
        return this.accessService.basis.catalogRights.canWriteCostCenter;
      case TypeFormEnum.AI_AGENT:
      case TypeFormEnum.AI_KNOWLEDGE_BASE:
        return this.accessService.basis.catalogRights.canWriteAi;
      case TypeFormEnum.DOCUMENT_FILE_TEMPLATE:
      case TypeFormEnum.DOCUMENT_FILE_TEMPLATE_KEY:
        return this.accessService.basis.catalogRights.canWriteDocumentFileTemplate;
      case TypeFormEnum.DATABASE_1C:
        return this.accessService.basis.companyRights.canWriteCompanyErp1C;
      case TypeFormEnum.COMPANY_GROUP:
        return this.accessService.basis.companyRights.canWriteCompanyGroup;
      case TypeFormEnum.NET_PING_DEVICE:
        return this.accessService.pacs.pacsRights.canWriteEquipment;
      case TypeFormEnum.PROJECT_TEMPLATE:
        return this.accessService.project.projectRights.canWriteTemplate;
      case TypeFormEnum.ACCESS_CARD:
        return this.accessService.pacs.pacsRights.canWriteAccessCard;
      case TypeFormEnum.USER_DELEGATION:
        return this.accessService.basis.securityRights.canWriteUser;
      case TypeFormEnum.DOCUMENT:
        return this.accessService.finance.documentRights.canWriteDocument;
      case TypeFormEnum.DOCUMENT_TEMPLATE:
        return this.accessService.finance.documentRights.canWriteTemplate;
      case TypeFormEnum.DOCUMENT_REGULATION:
        return this.accessService.finance.documentRights.canWriteBankStatementRule;
      case TypeFormEnum.DOCUMENT_DISTRIBUTION:
        return this.accessService.finance.documentRights.canWriteSalaryDistribution;
      case TypeFormEnum.SALARY_ACCRUAL:
        return this.accessService.finance.documentRights.canWriteSalaryAccrual;
      case TypeFormEnum.PAYMENT_TEMPLATE:
        return this.accessService.brs.brsTaskRights.canWriteTemplate;
      case TypeFormEnum.PAYMENT_STATUS:
        return this.accessService.brs.brsTaskRights.canWriteStatus;
      case TypeFormEnum.TASK_SCENARIO:
        return this.accessService.brs.brsTaskRights.canWritePaymentScenario;
      case TypeFormEnum.BALANCE_CUSTOM_ROW:
      case TypeFormEnum.BALANCE_REPORT_FORMAT:
        return this.accessService.finance.planFactRights.canWriteBalanceReport;
      case TypeFormEnum.PLANNING_SCENARIO:
        return this.accessService.finance.planFactRights.canWritePlanningScenario;
      case TypeFormEnum.NEWSLETTER:
      case TypeFormEnum.NEWSLETTER_TEMPLATE:
        return this.accessService.debts.debtsRights.canWriteMailing;
      case TypeFormEnum.DEBT_COLLECTION_TASK:
        return this.accessService.debts.debtsRights.canWriteDebtCollection;

      default:
        return true;
    }
  }
}
